import React, { useState, useRef, useMemo } from 'react';
import { animated, useSpring } from 'react-spring';
import { useDrag } from '@use-gesture/react';
import { BrowseCardResponse } from '@/types';
import { cn } from '@/utils';
import { TelegramUtils } from '@/utils/telegram';
import { getBadgeLevelColor, capitalizeBadgeLevel, getScoreColor } from '@/utils';

interface VerticalFeedCardProps {
  profile: BrowseCardResponse;
  onLikeToggle: () => void;
  onHide: () => void;
  onCardClick: () => void;
}

const VerticalFeedCard: React.FC<VerticalFeedCardProps> = ({
  profile,
  onLikeToggle,
  onHide,
  onCardClick,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Split content into panels
  const firstPhoto = profile.photos?.[0];
  const remainingPhotos = profile.photos?.slice(1) || [];
  const hasTechOrHobbies = (profile.tech_stack && profile.tech_stack.length > 0) || 
                           (profile.hobbies && profile.hobbies.length > 0);

  // Calculate total panels and their structure
  const panelStructure = useMemo(() => {
    const panels: Array<{ type: 'info' | 'photo', width: number, data?: any }> = [];
    
    // Panel 0: Basic info (60%)
    panels.push({ type: 'info', width: 0.6 });
    
    // Panel 1: First photo (100%) if exists
    if (firstPhoto) {
      panels.push({ type: 'photo', width: 1, data: firstPhoto });
    }
    
    // Panel 2: Tech/Hobbies (60%) if exist
    if (hasTechOrHobbies) {
      panels.push({ type: 'info', width: 0.6 });
    }
    
    // Panel 3+: Remaining photos (100% each)
    remainingPhotos.forEach(photo => {
      panels.push({ type: 'photo', width: 1, data: photo });
    });
    
    return panels;
  }, [firstPhoto, remainingPhotos, hasTechOrHobbies]);

  const totalPanels = panelStructure.length;
  
  // Helper function to calculate panel offset
  const getPanelOffset = (panelIndex: number): number => {
    if (panelIndex === 0) return 0;
    
    const cardWidth = containerRef.current?.offsetWidth || window.innerWidth;
    let offset = 0;
    
    // Sum up widths of all previous panels
    for (let i = 0; i < panelIndex; i++) {
      offset += cardWidth * panelStructure[i].width;
    }
    
    return -offset;
  };
  
  // Spring animation for horizontal scroll
  const [{ x }, api] = useSpring(() => ({
    x: 0,
    config: { tension: 250, friction: 30, clamp: true }
  }));

  // Navigate to specific panel
  const goToPanel = (index: number) => {
    if (index >= 0 && index < totalPanels) {
      setCurrentIndex(index);
      const targetX = getPanelOffset(index);
      api.start({ x: targetX });
      TelegramUtils.haptic.light();
    }
  };

  // Handle horizontal drag/swipe
  const bind = useDrag(
    ({ active, movement: [mx], direction: [xDir], distance: [dx], velocity, tap, event }) => {
      // Handle tap
      if (tap) {
        const target = event?.target as HTMLElement;
        
        // Check if tap is on the action buttons
        if (target?.closest('.action-button')) {
          return;
        }
        
        // Open full profile on tap
        onCardClick();
        return;
      }

      // Calculate swipe threshold
      const threshold = 50;
      
      if (!active) {
        // Check if we should switch panels
        const vx = velocity[0];
        if (dx > threshold || vx > 0.5) {
          if (xDir > 0 && currentIndex > 0) {
            // Swiped right - go to previous panel
            goToPanel(currentIndex - 1);
          } else if (xDir < 0 && currentIndex < totalPanels - 1) {
            // Swiped left - go to next panel
            goToPanel(currentIndex + 1);
          } else {
            // Bounce back
            goToPanel(currentIndex);
          }
        } else {
          // Not enough swipe - bounce back
          goToPanel(currentIndex);
        }
      } else {
        // During drag - follow finger
        const currentOffset = getPanelOffset(currentIndex);
        let targetX = currentOffset + mx;
        
        // Add resistance at the edges
        if ((currentIndex === 0 && mx > 0) || 
            (currentIndex === totalPanels - 1 && mx < 0)) {
          targetX = currentOffset + (mx * 0.2);
        }
        
        api.start({ x: targetX, immediate: true });
      }
    },
    {
      axis: 'x',
      filterTaps: true,
      rubberband: 0.2,
    }
  );

  // Update position when index changes
  React.useEffect(() => {
    const targetX = getPanelOffset(currentIndex);
    api.start({ x: targetX });
  }, [currentIndex, api]);

  return (
    <div 
      ref={containerRef}
      className="relative bg-black overflow-hidden"
      style={{ height: '400px' }}
    >
      <animated.div
        {...bind()}
        className="flex h-full cursor-grab active:cursor-grabbing select-none"
        style={{
          x,
          touchAction: 'pan-y pinch-zoom',
        }}
      >
        {/* Panel 1: Basic Information - 60% width */}
        <div 
          className="flex-shrink-0 h-full bg-gradient-to-br from-neutral-900 to-black p-6 overflow-y-auto"
          style={{ width: '60%' }}
        >
          {/* Name and Age */}
          <div className="mb-4">
            <h2 className="text-3xl font-bold text-white">
              {profile.first_name}, {profile.age}
            </h2>
            
            {/* Location and Distance */}
            {profile.city && (
              <div className="flex items-center gap-1.5 text-white/70 text-sm mt-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>
                  {profile.city}
                  {profile.distance_km && ` • ${Math.round(profile.distance_km)} км`}
                </span>
              </div>
            )}
          </div>
          
          {/* Professional Info for IT */}
          {profile.specializations && profile.specializations.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center gap-2 flex-wrap">
                {profile.verification_badge_level && (
                  <span className={cn(
                    'px-2.5 py-1 rounded-full text-xs font-semibold',
                    getBadgeLevelColor(profile.verification_badge_level)
                  )}>
                    {capitalizeBadgeLevel(profile.verification_badge_level)}
                  </span>
                )}
                <span className="text-white/80 text-sm font-medium">
                  {profile.specializations[0].name}
                </span>
                {profile.is_verified && profile.verification_score !== null && (
                  <span className={cn('font-bold text-sm', getScoreColor(profile.verification_score))}>
                    {profile.verification_score}/100
                  </span>
                )}
              </div>
            </div>
          )}
          
          {/* Bio */}
          {profile.bio && (
            <div className="mb-4">
              <p className="text-white/80 text-sm leading-relaxed">
                {profile.bio}
              </p>
            </div>
          )}
          
          {/* Lifestyle Tag */}
          {profile.lifestyle_tag && (
            <div className="mb-4">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 rounded-full text-white text-sm">
                {profile.lifestyle_tag.icon && <span>{profile.lifestyle_tag.icon}</span>}
                {profile.lifestyle_tag.name}
              </span>
            </div>
          )}
          
          {/* Meeting Goals */}
          {profile.meeting_goals && profile.meeting_goals.length > 0 && (
            <div className="mb-4">
              <div className="flex flex-wrap gap-2">
                {profile.meeting_goals.map((goal) => (
                  <span
                    key={goal.id}
                    className="px-2.5 py-1.5 bg-white/5 rounded-full text-white/70 text-xs font-medium"
                  >
                    {goal.icon && <span className="mr-1">{goal.icon}</span>}
                    {goal.name}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Panel 2: First Photo - 100% width */}
        {firstPhoto && (
          <div 
            className="flex-shrink-0 h-full relative bg-black"
            style={{ width: '100%' }}
          >
            <img
              src={firstPhoto.photo_url}
              alt={`${profile.first_name} 1`}
              className="w-full h-full object-cover"
              draggable={false}
            />
          </div>
        )}
        
        {/* Panel 3: Tech Stack & Hobbies - 60% width (if exist) */}
        {hasTechOrHobbies && (
          <div 
            className="flex-shrink-0 h-full bg-gradient-to-br from-neutral-900 to-black p-6 overflow-y-auto"
            style={{ width: '60%' }}
          >
            {/* Tech Stack */}
            {profile.tech_stack && profile.tech_stack.length > 0 && (
              <div className="mb-6">
                <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Технологии</p>
                <div className="flex flex-wrap gap-1.5">
                  {profile.tech_stack.map((tech) => (
                    <span
                      key={tech.id}
                      className="px-2 py-1 bg-white/5 rounded-lg text-white/70 text-xs"
                    >
                      {tech.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Hobbies with common highlight */}
            {profile.hobbies && profile.hobbies.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-white/50 uppercase tracking-wider mb-2">Интересы</p>
                <div className="flex flex-wrap gap-1.5">
                  {profile.hobbies.map((hobby) => {
                    const isCommon = profile.common_hobbies?.some(ch => ch.id === hobby.id);
                    return (
                      <span
                        key={hobby.id}
                        className={cn(
                          'px-2 py-1 rounded-lg text-xs',
                          isCommon
                            ? 'bg-primary-500/20 text-primary-400 font-semibold border border-primary-500/30'
                            : 'bg-white/5 text-white/70'
                        )}
                      >
                        {isCommon && '✨ '}
                        {hobby.name}
                      </span>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Panel 4+: Remaining Photos - 100% width each */}
        {remainingPhotos.map((photo, index) => (
          <div 
            key={photo.id}
            className="flex-shrink-0 h-full relative bg-black"
            style={{ width: '100%' }}
          >
            <img
              src={photo.photo_url}
              alt={`${profile.first_name} ${index + 2}`}
              className="w-full h-full object-cover"
              draggable={false}
            />
          </div>
        ))}
        
        {/* If no photos at all, show placeholder */}
        {(!profile.photos || profile.photos.length === 0) && (
          <div 
            className="flex-shrink-0 h-full bg-gradient-to-br from-neutral-800 to-neutral-900 flex items-center justify-center"
            style={{ width: '100%' }}
          >
            <div className="text-center">
              <svg className="w-20 h-20 text-white/20 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <p className="text-white/40 text-sm">Нет фотографий</p>
            </div>
          </div>
        )}
      </animated.div>

      {/* Action Buttons - Fixed position */}
      <div className="absolute bottom-6 right-6 z-30 flex items-center gap-3">
        {/* Hide Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onHide();
          }}
          className={cn(
            'action-button',
            'w-14 h-14 rounded-full flex items-center justify-center',
            'transition-all transform active:scale-95 shadow-xl',
            'bg-neutral-800/90 backdrop-blur-sm hover:bg-neutral-700'
          )}
          title="Скрыть пользователя"
        >
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88"
            />
          </svg>
        </button>

        {/* Like Button */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            onLikeToggle();
          }}
          className={cn(
            'action-button',
            'w-14 h-14 rounded-full flex items-center justify-center',
            'transition-all transform active:scale-95 shadow-xl',
            profile.swipe_status === 'like'
              ? 'bg-gradient-to-r from-pink-500 to-red-500'
              : 'bg-white/90 backdrop-blur-sm hover:bg-white'
          )}
          title="Лайк"
        >
          <svg
            className={cn(
              'w-7 h-7 transition-colors',
              profile.swipe_status === 'like' ? 'text-white' : 'text-neutral-900'
            )}
            fill={profile.swipe_status === 'like' ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default VerticalFeedCard;
